import React from 'react';

import {
  ChevronRight as ChevronRightIcon,
  StickyNote2Outlined as StickyNoteIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Box,
  Paper,
  svgIconClasses,
  Typography,
} from '@mui/material';

import { type EmployeeDataInfoFragment } from 'src/api';
import { useEmployeeNotes } from 'src/hooks/employeeNote';
import type { SxProp } from 'src/types/mui';

import { EmployeeDataInternalNotesCardForm } from './EmployeeDataInternalNotesCardForm/EmployeeDataInternalNotesCardForm';
import { EmployeeDataInternalNotesCardItem } from './EmployeeDataInternalNotesCardItem';

type EmployeeDataInternalNotesCardProps = {
  entry: EmployeeDataInfoFragment;
  employeeSsnEncrypted: string;
};

export const EmployeeDataInternalNotesCard: React.FC<EmployeeDataInternalNotesCardProps> = ({
  entry,
  employeeSsnEncrypted,
}) => {
  // For an MDM-only person there is no CV Employee yet, so entry.id is
  // null and the query is skipped. Creating the first note lazily creates the
  // Employee server-side; adopt the returned employeeId so the list can load.
  const [employeeId, setEmployeeId] = React.useState(entry.id);

  const { employeeNotes, loading, createEmployeeNote } = useEmployeeNotes({
    variables: {
      orderBy: { createdAt: 'Desc' },
      where: {
        employeeId: { equals: employeeId },
      },
    },
    skip: !employeeId,
  });

  const handleCreate = React.useCallback(
    async (comment: string) => {
      const employeeNote = await createEmployeeNote(employeeSsnEncrypted, comment);
      if (!employeeId && employeeNote) {
        setEmployeeId(employeeNote.employeeId);
      }
    },
    [createEmployeeNote, employeeSsnEncrypted, employeeId],
  );

  const employeeNotesCount = employeeNotes.length;

  return (
    <Accordion
      component={Paper}
      variant="outlined"
      sx={accordionStyles}
      slotProps={{ heading: { component: 'div' } }}
      disabled={loading}
    >
      <AccordionSummary sx={accordionSummaryStyles} expandIcon={<ChevronRightIcon />}>
        <Box>
          <Typography variant="body1">Internal admin notes</Typography>
          {employeeNotesCount === 0 ? (
            <Typography variant="caption">No notes added yet</Typography>
          ) : (
            <Typography variant="caption" color="warning.dark" sx={notesCountStyles}>
              <StickyNoteIcon sx={notesCountIconStyles} />
              {employeeNotesCount} note{employeeNotesCount > 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={accordionDetailsStyles}>
        <EmployeeDataInternalNotesCardForm onCreate={handleCreate} />

        <Box mt={2}>
          {employeeNotes.map(employeeNote => (
            <EmployeeDataInternalNotesCardItem key={employeeNote.id} employeeNote={employeeNote} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const accordionStyles: SxProp = {
  paddingX: 3,
  boxShadow: 'unset',
  borderRadius: '10px',
  borderBottomLeftRadius: '10px !important',
  borderBottomRightRadius: '10px !important',
  '&::before': {
    display: 'contents',
  },
};

const accordionSummaryStyles: SxProp = {
  padding: 0,
  [`.${accordionSummaryClasses.expandIconWrapper} .${svgIconClasses.root}`]: {
    fontSize: '1.25rem',
  },
};

const notesCountStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
};

const notesCountIconStyles: SxProp = {
  fontSize: '1rem',
};

const accordionDetailsStyles: SxProp = {
  paddingX: 0,
  paddingTop: 0,
  paddingBottom: 3,
};
